/**
 * Where the identity-provider settings come from, per surface.
 *
 * Two sources, because the two surfaces are built differently and this has bitten
 * the telemetry config before:
 *
 * - **Web** reads `window.__CYNOSURE_CONFIG__`, injected into index.html by the
 *   backend at startup (backend/app/main.py). One image serves every cluster, so
 *   a build-time value would be wrong somewhere.
 * - **Native** reads `EXPO_PUBLIC_*`, baked in per EAS profile (eas.json). One
 *   binary talks to whichever cluster DNS points at, so these are baked once.
 *
 * Both values are PUBLIC. A PKCE client has no secret by construction — that is
 * the entire reason the mobile and web clients are public clients — so the
 * client id shipping inside every bundle is the design, not a leak.
 *
 * An empty issuer means IdP login is off. That is the default, and it is what
 * keeps this additive: nothing here runs until a cluster sets OIDC_ISSUER.
 */

import { Platform } from 'react-native';

interface RuntimeConfig {
  oidcIssuer?: string;
  oidcClientId?: string;
}

function runtimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined') return {};
  return (window as unknown as { __CYNOSURE_CONFIG__?: RuntimeConfig }).__CYNOSURE_CONFIG__ ?? {};
}

export interface OidcConfig {
  issuer: string;
  clientId: string;
}

/** Resolved IdP settings, or null when IdP login is not configured. */
export function getOidcConfig(): OidcConfig | null {
  // Native first: on web these env vars are absent, and on native the runtime
  // config global does not exist, so the two never contend.
  const issuer = process.env.EXPO_PUBLIC_OIDC_ISSUER || runtimeConfig().oidcIssuer || '';
  const clientId = process.env.EXPO_PUBLIC_OIDC_CLIENT_ID || runtimeConfig().oidcClientId || '';

  if (!issuer || !clientId) return null;
  return { issuer: issuer.replace(/\/$/, ''), clientId };
}

export function isOidcEnabled(): boolean {
  return getOidcConfig() !== null;
}

/**
 * The redirect target, which MUST byte-match one registered on the ZITADEL app
 * (a3rn-fleet apps/base/zitadel/scripts/configure.py). A mismatch fails at
 * /authorize with invalid_request, before any login UI appears.
 *
 * Native uses the custom scheme from app.json, which is why that client is
 * registered as OIDC_APP_TYPE_NATIVE — ZITADEL only accepts custom schemes
 * there. Web uses the current origin, so a build works at localhost during
 * development and at app-cynosure.a3rn.com in production without a rebuild.
 */
export function getRedirectUri(): string {
  if (Platform.OS === 'web') {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/auth/callback`;
  }
  return 'cynosure://auth/callback';
}

/**
 * `offline_access` is what asks for a refresh token; without it the session
 * ends when the access token expires and the user is bounced back to the IdP.
 *
 * Note there is deliberately no ZITADEL role scope here. Roles reach the token
 * because the project sets projectRoleAssertion and the apps set
 * accessTokenRoleAssertion — server-side configuration, not something a client
 * asks for, which is what stops a client from choosing its own authorization.
 */
export const OIDC_SCOPES = ['openid', 'profile', 'email', 'offline_access'];
